<template>
  <section class="hero">
    <div class="hero-content">
      <h1>{{ pageTitle }}</h1>
      <p>{{ pageSubtitle }}</p>
      <div class="search-box-container">
        <SearchBox />
      </div>
    </div>
  </section>
  <div class="page-container">
    <!-- Résultats de recherche -->
    <template v-if="hasSearchParams">
      <LoaderOverlay v-if="loading" />
      <EmptyState v-else-if="error || noResults" :error="error" :is-empty="noResults" />
      <template v-else>
        <TripFilters :trips="store.trips" @update:filtered="filteredTrips = $event" />
        <TripList :trips="filteredTrips" />
      </template>
    </template>

    <!-- Page d'accueil sans paramètres -->
    <template v-else>
      <div class="page-content">
        <EmptyState :is-empty="true" />
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useSearchStore } from '@/stores/searchStore'
import { sleep } from '@/utils/sleep'
import SearchBox from '@/components/SearchBox.vue'
import TripList from '@/components/TripList.vue'
import LoaderOverlay from '@/components/LoaderOverlay.vue'
import EmptyState from '@/components/EmptyState.vue'
import { config } from '@/config/env.js'
import TripFilters from '@/components/TripFilters.vue'

const route = useRoute()
const store = useSearchStore()

const loading = ref(false)
const error = ref(null)
const filteredTrips = ref([])

const hasSearchParams = computed(() => {
  if (route.query.startCity && route.query.endCity && route.query.dateToStart) return true
  if (route.params.startCity || route.params.endCity) return true
  return false
})

const startCity = computed(() => route.query.startCity || route.params.startCity || '')
const endCity = computed(() => route.query.endCity || route.params.endCity || '')
const dateToStart = computed(() => route.query.dateToStart || route.params.date || '')

const noResults = computed(() =>
  hasSearchParams.value && store.trips.length === 0 && !loading.value && !error.value
)

const pageTitle = computed(() => {
  if (startCity.value && !endCity.value) return `Trajets au départ de ${startCity.value}`
  if (endCity.value && !startCity.value) return `Trajets vers ${endCity.value}`
  if (hasSearchParams.value) return 'Résultats de votre recherche'
  return 'Rechercher un trajet'
})

const pageSubtitle = computed(() =>
  hasSearchParams.value ? 'Affinez votre recherche si nécessaire' : 'Où souhaitez-vous aller ?'
)

watch(() => store.trips, (val) => {
  filteredTrips.value = val
}, { immediate: true })

async function loadTrips(start, end, date) {
  if (!start || !end) return
  loading.value = true
  error.value = null
  const minTime = sleep(800)

  try {
    const body = {
      startCity: start,
      endCity: end,
    }

    if (date) {
      body.dateToStart = `${date}T00:00:00`
      body.dateToEnd = `${date}T23:59:59`
    }

    const response = await fetch(`${config.apiUrl}/trips/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => null)
      throw new Error(errorData?.message || `Erreur ${response.status}`)
    }

    const data = await response.json()
    store.setSearchResults(Array.isArray(data) ? data : [], {
      startCity: start,
      endCity: end,
      dateToStart: date
    })
  } catch (e) {
    error.value = e.message || 'Erreur lors de la recherche'
    store.setSearchResults([], { startCity: start, endCity: end, dateToStart: date })
  } finally {
    await minTime
    loading.value = false
  }
}

async function loadTripsByStartCity(city) {
  if (!city) return
  loading.value = true
  error.value = null
  const minTime = sleep(800)

  try {
    const response = await fetch(`${config.apiUrl}/trips/start/${encodeURIComponent(city)}`)

    if (!response.ok) {
      const errorData = await response.json().catch(() => null)
      throw new Error(errorData?.message || `Erreur ${response.status}`)
    }

    const data = await response.json()
    store.setSearchResults(Array.isArray(data) ? data : [], {
      startCity: city,
      endCity: '',
      dateToStart: ''
    })
  } catch (e) {
    error.value = e.message || 'Erreur lors de la recherche'
    store.setSearchResults([], { startCity: city, endCity: '', dateToStart: '' })
  } finally {
    await minTime
    loading.value = false
  }
}

async function loadTripsByArrivalCity(city) {
  if (!city) return
  loading.value = true
  error.value = null
  const minTime = sleep(800)

  try {
    const response = await fetch(`${config.apiUrl}/trips/arrival/${encodeURIComponent(city)}`)

    if (!response.ok) {
      const errorData = await response.json().catch(() => null)
      throw new Error(errorData?.message || `Erreur ${response.status}`)
    }

    const data = await response.json()
    store.setSearchResults(Array.isArray(data) ? data : [], {
      startCity: '',
      endCity: city,
      dateToStart: ''
    })
  } catch (e) {
    error.value = e.message || 'Erreur lors de la recherche'
    store.setSearchResults([], { startCity: '', endCity: city, dateToStart: '' })
  } finally {
    await minTime
    loading.value = false
  }
}

function loadData() {
  const start = startCity.value
  const end = endCity.value
  const date = dateToStart.value

  if (start && end) loadTrips(start, end, date || null)
  else if (start && !end) loadTripsByStartCity(start)
  else if (!start && end) loadTripsByArrivalCity(end)
  // else : page d'accueil, rien à charger, EmptyState affiché
}

onMounted(loadData)

watch(() => [startCity.value, endCity.value, dateToStart.value], loadData)
</script>

<style scoped>
.page-container {
  width: 100%;
  max-width: 1100px;
}

.page-header {
  padding-top: 0;
  width: 100%;
}

.page-content {
  gap: 2rem;
  width: 100%;
}

.latest-trips {
  display: flex;
  flex-flow: column wrap;
  gap: 3rem;
}

@media (max-width: 768px) {
  .hero-content h1 {
    padding: 0 1.5rem !important;
  }

  .hero {
    margin-bottom: 1rem;
  }
}
</style>
